import { useQueryRetirementCalculator } from '@/api'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import {
  StmtRetirementCalculatorReqBodyPayload,
  StmtRetirementCalculatorResBodyPayloadRetirementCalculatorResponse,
} from '@pimy-b2cweb/apiclient-customer-stmt'
import { Button, FormLabel, Slider, Switch, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'

const RetirementCalculator = () => {
  const { t } = useTranslation(['statementPage', 'common'])

  const { register, handleSubmit, watch, setValue, getValues, control } =
    useForm({
      defaultValues: {
        amount: 3000,
        currentAge: 25,
        retiredAge: 60,
        annReturnsBeforeRetired: 4,
        annReturnsAfterRetired: 2,
        annualInflation: 2,
        annualLifeExpectancy: 8,
        monthly: true,
      },
    })

  const [expenseAmountAtRetirement, setExpenseAmountAtRetirement] = useState(0)
  const [savingsBeforeRetirement, setSavingsBeforeRetirement] = useState(0)

  const watchMonthly = useWatch({ control, name: 'monthly' })
  const {
    mutate: calculateRetirement,
    data,
    isSuccess,
  } = useMutation({
    mutationFn: useQueryRetirementCalculator,
    onSuccess: (
      data:
        | StmtRetirementCalculatorResBodyPayloadRetirementCalculatorResponse
        | undefined
    ) => {
      const selected = watchMonthly ? data?.monthly : data?.yearly
      setExpenseAmountAtRetirement(
        selected?.expenseAmountNeededAtRetirement ?? 0
      )
      setSavingsBeforeRetirement(selected?.savingsNeededBeforeRetirement ?? 0)
    },
  })

  useEffect(() => {
    if (isSuccess && data) {
      const result = getValues('monthly') ? data.monthly : data.yearly
      setExpenseAmountAtRetirement(result?.expenseAmountNeededAtRetirement || 0)
      setSavingsBeforeRetirement(result?.savingsNeededBeforeRetirement || 0)
    }
  }, [isSuccess, data, getValues])

  useEffect(() => {
    if (data) {
      const selected = watchMonthly ? data.monthly : data.yearly
      setExpenseAmountAtRetirement(
        selected?.expenseAmountNeededAtRetirement ?? 0
      )
      setSavingsBeforeRetirement(selected?.savingsNeededBeforeRetirement ?? 0)
    }
  }, [data, watchMonthly])

  const onSubmit = (formData: StmtRetirementCalculatorReqBodyPayload) => {
    const payload = {
      amount: formData.amount ?? 0,
      currentAge: formData.currentAge ?? 0,
      retiredAge: formData.retiredAge ?? 0,
      annReturnsBeforeRetired: formData.annReturnsBeforeRetired ?? 0,
      annReturnsAfterRetired: formData.annReturnsAfterRetired ?? 0,
      annualInflation: formData.annualInflation ?? 0,
      annualLifeExpectancy: formData.annualLifeExpectancy ?? 0,
      monthly: formData.monthly ?? true,
    }
    calculateRetirement(payload)
  }

  type AssumptionField =
    | 'annReturnsBeforeRetired'
    | 'annReturnsAfterRetired'
    | 'annualInflation'
    | 'annualLifeExpectancy'

  const assumptionsLables: Record<AssumptionField, string> = {
    annReturnsBeforeRetired: 'Annual Returns Before Retirement (%)',
    annReturnsAfterRetired: 'Annual Returns After Retirement (%)',
    annualInflation: 'Annual Inflaton Rate (%)',
    annualLifeExpectancy: 'Life Expectancy After Retirement',
  }

  const assumptionsKeys: AssumptionField[] = [
    'annReturnsBeforeRetired',
    'annReturnsAfterRetired',
    'annualInflation',
    'annualLifeExpectancy',
  ]

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Life Styles Expenses */}
      <div className='space-y-6 p-6'>
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-6'>
          <div className='flex flex-col items-center space-y-2'>
            <img
              src={require('../../assets/expense-icon.png')}
              alt='Lifestyle Expenses'
              className='w-20 h-20'
            />
            <FormLabel className='text-center font-semibold'>
              {t('Expenses based on todays lifestyle (RM)')}
            </FormLabel>
            <TextField
              type='number'
              className='border rounded px-2 py-1 w-40'
              {...register('amount', { valueAsNumber: true })}
              size='small'
            />
          </div>
          {/* Current Age */}
          <div className='flex flex-col items-center space-y-2'>
            <img
              src={require('../../assets/currentAge-icon.png')}
              alt='Current Age'
              className='w-20 h-20'
            />
            <FormLabel className='text-center font-semibold'>
              {t('Current Age')}
            </FormLabel>
            <TextField
              type='number'
              className='border rounded px-2 py-1 w-40'
              {...register('currentAge', { valueAsNumber: true })}
              size='small'
            />
            <Slider
              min={18}
              max={100}
              onChange={(e, value) => setValue('currentAge', value as number)}
              className='w-40 accent-pi-utility-red'
              value={watch('currentAge')}
            />
          </div>
          {/* Retirement Age */}
          <div className='flex flex-col items-center space-y-2'>
            <img
              src={require('../../assets/retirementAge-icon.png')}
              alt='Retirement Age'
              className='w-20 h-20'
            />
            <FormLabel className='text-center font-semibold'>
              {t('Age at Retirement')}
            </FormLabel>
            <TextField
              type='number'
              className='border rounded px-2 py-1 w-40'
              {...register('retiredAge', { valueAsNumber: true })}
              size='small'
            />
            <Slider
              min={0}
              max={100}
              onChange={(e, value) => setValue('retiredAge', value as number)}
              className='w-40 accent-pi-utility-red'
              value={watch('retiredAge')}
            />
          </div>
        </div>
      </div>

      {/* Assumptions */}
      <div>
        <div className='font-bold text-center text-xl mb-4 bg-yellow-200 py-2'>
          {t('Assumptions')}
        </div>
        <div className='flex flex-col gap-4'>
          {assumptionsKeys.map((key) => (
            <div
              key={key}
              className='flex flex-col sm:flex-row sm:items-center gap-4'
            >
              <FormLabel className='sm:w-1/3 font-semibold capitalize'>
                {assumptionsLables[key]}
              </FormLabel>
              <TextField
                type='number'
                className='sm:w-1/3 border rounded px-2 py-1'
                {...register(key, { valueAsNumber: true })}
                size='small'
              />
              <Slider
                min={0}
                max={100}
                className='sm:w-1/3 accent-pi-utility-red'
                value={watch(key) ?? 0}
                onChange={(e, value) => setValue(key, value as number)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Submit */}
      <div className='text-center mt-6'>
        <Button
          type='submit'
          variant='contained'
          className='bg-pi-true-blue text-white font-semibold px-6 py-2 rounded-lg shadow hover:bg-pi-navy-blue'
        >
          {t('Submit')}
        </Button>
      </div>

      {/* Monthly & Yearly */}
      <div className='grid grid-cols-1 sm:grid-cols-3 items-start mt-10'>
        <div className='col-span-1 flex items-center gap-4'>
          <FormLabel className='font-semibold'>Monthly</FormLabel>
          <Switch
            checked={!watchMonthly}
            onChange={() => setValue('monthly', !watch('monthly'))}
          />
          <FormLabel className='font-semibold'>Yearly</FormLabel>
        </div>

        {/* Results */}
        <div className='col-span-1 space-y-4'>
          <div>
            <FormLabel className='font-semibold pb-2 block'>
              {t('Expense Amount at Retirement (RM)')}
            </FormLabel>
            <TextField
              variant='outlined'
              inputProps={{ readOnly: true }}
              className='border rounded px-2 py-1 w-full'
              value={expenseAmountAtRetirement ?? 0}
              size='small'
            />
          </div>
          <div>
            <FormLabel className='font-semibold pb-2 block'>
              {t('Savings Needed at Retirement (RM)')}
            </FormLabel>
            <TextField
              variant='outlined'
              inputProps={{ readOnly: true }}
              className='border rounded px-2 py-1 w-full'
              value={savingsBeforeRetirement ?? 0}
              size='small'
            />
          </div>
        </div>
      </div>
    </form>
  )
}
export default RetirementCalculator
